import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { isContractActive } from '@/utils/contractUtils';

import FormModal from '@/components/v2/molecules/admin/shared/FormModal';

import {

  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

import {
  useCreateBusinessIncome,
  useUpdateBusinessIncome,
} from '@/hooks/useBusinessIncome';

import { PaginatedBusinessIncome } from '@/components/v2/organisms/admin/tabs/BusinessIncomeTab/BusinessIncomeTable';

import {
  BUSINESS_INCOME_TYPES,
  BUSINESS_PAYMENT_TYPES,
} from '@/constants/businessIncomes';

import { useProperties } from '@/hooks/useProperties';
import { useOrganizations } from '@/hooks/useOrganizations';
import { useContracts } from '@/hooks/useContracts';

import {
  createBusinessIncomeSchema,
  type CreateBusinessIncomeData,
} from '@/schemas/admin/BusinessIncomeTab/createBusinessIncomeSchema';

import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import CustomDatePicker from '@/components/CustomDatePicker';
import { format } from 'date-fns';
import { parseDateFromDatabase } from '@/utils/dateParser';
interface BusinessIncomeFormModalProps {
  isOpen: boolean;
  onClose: (open: boolean) => void;
  editingIncome: PaginatedBusinessIncome | null;
}

const BusinessIncomeFormModal = ({
  isOpen,
  onClose,
  editingIncome,
}: BusinessIncomeFormModalProps) => {
  const { data: properties = [], error: propertiesError } = useProperties();
  const { data: organizations = [] } = useOrganizations();
  const { data: contracts = [] } = useContracts();
  const createIncome = useCreateBusinessIncome();
  const updateIncome = useUpdateBusinessIncome();

  const defaultValues: CreateBusinessIncomeData = {
    organization_id: '',
    property_id: "none",
    income_type: '',
    payment_type: 'Transferencia',
    gross_payment: undefined,
    iva: undefined,
    total_payment: undefined,
    payment_status: 'Pendiente',
    description: '',
    payment_date: new Date(),
  };

  const form = useForm<CreateBusinessIncomeData>({
    resolver: zodResolver(createBusinessIncomeSchema),
    defaultValues,
    mode: 'onChange',
  });

  // Observar organización seleccionada
  const selectedOrganizationId = form.watch("organization_id");

  // Filtrar propiedades por organización seleccionada
  const filteredProperties = useMemo(() => {
    if (!selectedOrganizationId) {
      return []; // Sin organización seleccionada, no mostrar propiedades
    }

    // Obtener property_ids de contratos activos de la organización
    const orgPropertyIds = contracts
      .filter(c => c.organization_id === selectedOrganizationId && isContractActive(c))
      .map(c => c.property_id);

    // Filtrar propiedades que pertenecen a la organización
    return properties.filter(p => orgPropertyIds.includes(p.id));
  }, [selectedOrganizationId, contracts, properties]);

  const isDisabled =
    !form.formState.isValid || createIncome.isPending || updateIncome.isPending;

  const onSubmit = async (data: CreateBusinessIncomeData) => {
    try {
      const payload = {
        organization_id: data.organization_id || null,
        property_id: data.property_id === "none" ? null : data.property_id,
        income_type: data.income_type as (typeof BUSINESS_INCOME_TYPES)[number],
        payment_type: data.payment_type,
        gross_payment: data.gross_payment,
        iva: data.iva,
        total_payment: data.total_payment,
        payment_status: data.payment_status,
        description: data.description,
        payment_date: data.payment_date
          ? format(data.payment_date, 'yyyy-MM-dd')
          : undefined,
      };

      if (editingIncome) {
        await updateIncome.mutateAsync({ id: editingIncome.id, ...payload });
      } else {
        await createIncome.mutateAsync(payload);
      }

      form.reset();
      onClose(false);
    } catch (error) {
      console.error('Error creating business income:', error);
    }
  };

  useEffect(() => {
    if (editingIncome && isOpen) {
      form.reset({
        organization_id: editingIncome.organization_id ?? '',
        property_id: editingIncome.property_id ?? "none",
        income_type: editingIncome.income_type as any,
        payment_type: editingIncome.payment_type as "Efectivo" | "Transferencia" | "Deducción",
        gross_payment: editingIncome.gross_payment ?? undefined,
        iva: editingIncome.iva ?? undefined,
        total_payment: editingIncome.total_payment ?? 0,
        payment_status: editingIncome.payment_status as "Pendiente" | "Pagado" | "Cancelado",
        description: editingIncome.description ?? '',
        payment_date: editingIncome.payment_date
          ? parseDateFromDatabase(editingIncome.payment_date) ?? undefined
          : undefined,
      });
    }
  }, [editingIncome, isOpen]);

  useEffect(() => {
    if (!isOpen) {
      form.reset(defaultValues);
    }
  }, [isOpen]);

  // Resetear property_id cuando cambia la organización
  useEffect(() => {
    const currentPropertyId = form.getValues("property_id");
    if (currentPropertyId && currentPropertyId !== "none") {
      const isValidProperty = filteredProperties.some(p => p.id === currentPropertyId);
      if (!isValidProperty) {
        form.setValue("property_id", "none");
      }
    }
  }, [selectedOrganizationId, filteredProperties, form]);

  return (
    <FormModal
      isOpen={isOpen}
      onClose={() => onClose(false)}
      title={editingIncome ? 'Editar Ingreso del Negocio' : 'Nuevo Ingreso del Negocio'}
      form={form}
      onSubmit={onSubmit}
      isLoading={isDisabled}
      submitLabel={editingIncome
        ? updateIncome.isPending
          ? 'Guardando...'
          : 'Guardar Cambios'
        : createIncome.isPending
          ? 'Creando...'
          : 'Crear Ingreso'}
      maxWidth="max-w-lg"
    >
      <div className="space-y-4">
        {/* Organización - Full width */}
        <FormField
          control={form.control}
          name="organization_id"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Organización {!editingIncome && '*'}</FormLabel>
              <Select onValueChange={field.onChange} value={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar organización" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {organizations.map((org) => (
                    <SelectItem key={org.id} value={org.id}>
                      {org.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Propiedad - Full width */}
        <FormField
          control={form.control}
          name="property_id"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Propiedad (Opcional)</FormLabel>
              <Select onValueChange={field.onChange} value={field.value || "none"}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar propiedad (opcional)" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="none">Sin propiedad específica</SelectItem>
                  {propertiesError ? (
                    <SelectItem value="error" disabled>
                      Error al cargar propiedades
                    </SelectItem>
                  ) : (
                    filteredProperties.map((property) => (
                      <SelectItem key={property.id} value={property.id}>
                        {property.shortName}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Tipo de ingreso y Tipo de pago - Same line */}
        <div className="grid grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="income_type"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Tipo de Ingreso *</FormLabel>
                <Select onValueChange={field.onChange} value={field.value}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Seleccionar tipo" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {BUSINESS_INCOME_TYPES.map((type) => (
                      <SelectItem key={type} value={type}>
                        {type}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="payment_type"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Tipo de Pago *</FormLabel>
                <Select onValueChange={field.onChange} value={field.value}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Seleccionar tipo de pago" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {BUSINESS_PAYMENT_TYPES.map((type) => (
                      <SelectItem key={type} value={type}>
                        {type}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {/* Pago bruto e IVA - Same line */}
        <div className="grid grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="gross_payment"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Pago Bruto</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    {...field}
                    value={field.value ?? ""}
                    onChange={(e) =>
                      field.onChange(e.target.value === "" ? undefined : parseFloat(e.target.value))
                    }
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="iva"
            render={({ field }) => (
              <FormItem>
                <FormLabel>IVA</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    {...field}
                    value={field.value ?? ""}
                    onChange={(e) =>
                      field.onChange(e.target.value === "" ? undefined : parseFloat(e.target.value))
                    }
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {/* Pago total y Estatus de pago - Same line */}
        <div className="grid grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="total_payment"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Pago Total *</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    {...field}
                    value={field.value ?? ""}
                    onChange={(e) =>
                      field.onChange(e.target.value === "" ? undefined : parseFloat(e.target.value))
                    }
                    className="placeholder:text-muted-foreground"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="payment_status"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Estatus del Pago *</FormLabel>
                <Select onValueChange={field.onChange} value={field.value}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Seleccionar estatus" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="Pendiente">Pendiente</SelectItem>
                    <SelectItem value="Pagado">Pagado</SelectItem>
                    <SelectItem value="Cancelado">Cancelado</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {/* Descripción - Full width */}
        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Descripción</FormLabel>
              <FormControl>
                <Input
                  placeholder="Descripción del ingreso"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Fecha de pago - Full width */}
        <FormField
          control={form.control}
          name="payment_date"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Fecha de Pago</FormLabel>
              <FormControl>
                <CustomDatePicker
                  selected={field.value}
                  onSelect={field.onChange}
                  placeholder="Seleccionar fecha"
                  className="w-full h-fit"
                  isAdminContext
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    </FormModal>
  );
};

export default BusinessIncomeFormModal;
